import React, { createContext, useContext, useState, useEffect } from 'react';

// Multi-language support context
const LanguageContext = createContext();

// Language configurations
const SUPPORTED_LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    rtl: false
  },
  es: {
    code: 'es', 
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    rtl: false
  },
  ar: {
    code: 'ar',
    name: 'Arabic', 
    nativeName: 'العربية',
    flag: '🇸🇦',
    rtl: true
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français', 
    flag: '🇫🇷',
    rtl: false
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    rtl: false
  },
  tr: {
    code: 'tr',
    name: 'Turkish',
    nativeName: 'Türkçe',
    flag: '🇹🇷',
    rtl: false
  },
  nl: {
    code: 'nl',
    name: 'Dutch',
    nativeName: 'Nederlands',
    flag: '🇳🇱',
    rtl: false
  }
};

// Translations object
const TRANSLATIONS = {
  en: {
    // Main Navigation
    'nav.threat_scanner': 'Threat Scanner',
    'nav.galaxy_threat_map': 'Galaxy Threat Map',
    'nav.admin_control': 'Admin Control',
    'nav.partner_portal': 'Partner Portal',
    'nav.documentation': 'Documentation',
    'nav.sdk_center': 'SDK Center',
    
    // Main Interface
    'main.title': 'AI Threat Scanner',
    'main.subtitle': 'Real-time protection against scams and threats',
    'main.tabs.text': 'Text Analysis',
    'main.tabs.link': 'Link Scanner',
    'main.tabs.image': 'Image Analysis',
    
    // Text Analysis
    'text.placeholder': 'Paste suspicious message or email content here...',
    'text.button': 'Scan for Threats',
    'text.loading': 'Analyzing content...',
    
    // Link Analysis
    'link.placeholder': 'Enter suspicious URL or link...',
    'link.button': 'Scan Link',
    'link.loading': 'Checking link safety...',
    
    // Image Analysis
    'image.upload': 'Upload Screenshot',
    'image.button': 'Analyze Image',
    'image.loading': 'Processing image...',
    
    // Results
    'results.threat_detected': 'Threat Detected',
    'results.safe': 'Content appears safe',
    'results.confidence': 'Confidence',
    'results.threat_type': 'Threat Type',
    'results.risk_level': 'Risk Level',
    'results.recommendations': 'Recommendations',
    
    // Voice Protection
    'voice.title': 'Real-Time Voice Protection',
    'voice.status_active': 'Protected',
    'voice.status_inactive': 'Inactive',
    'voice.status_message_active': 'Live detection is running. You\'re protected.',
    'voice.status_message_inactive': 'Voice protection is paused',
    'voice.button_start': 'Start Protection',
    'voice.button_pause': 'Pause Protection',
    'voice.button_settings': 'Settings',
    'voice.button_alert_log': 'Alert Log',
    
    // Consent Dialog
    'consent.title': 'Enable Real-Time Voice Protection?',
    'consent.description': 'Veridavo can protect you from scam calls in real-time by analyzing voice patterns and detecting suspicious behavior.',
    'consent.feature_realtime': 'Real-time scam detection',
    'consent.feature_ondevice': 'On-device processing when possible',
    'consent.feature_nostorage': 'No audio stored unless you choose',
    'consent.button_enable': 'Enable Protection',
    'consent.button_ask': 'Ask Every Time',
    'consent.button_cancel': 'Cancel',
    'consent.privacy_link': 'Privacy & Detection Info',
    
    // Retention Dashboard
    'retention.streak.title': 'Daily Streak',
    'retention.streak.start': 'Start your protection streak today!',
    'retention.streak.day1': 'Great start! Keep it going!',
    'retention.streak.week': 'Week warrior! {days} days protected!',
    'retention.streak.month': 'Month master! {days} days of protection!',
    'retention.level.title': 'Protection Level',
    'retention.report.title': 'Your Protection Report',
    'retention.report.description': 'Get insights into your scam protection activity',
    'retention.report.button': 'Generate Weekly Report',
    'retention.tip.title': 'Daily Protection Tip',
    
    // Social Growth
    'growth.share.title': 'Share This Detection',
    'growth.share.description': 'Help protect others by sharing this scam detection!',
    'growth.impact.title': 'Your Growth Impact',
    'growth.referral.title': 'Refer Friends & Earn Rewards',
    'growth.community.title': 'Community Impact',
    
    // Premium Features
    'premium.title': 'Upgrade to Premium',
    'premium.feature_realtime': 'Real-time call protection',
    'premium.feature_notifications': 'Push notifications for scam calls',
    'premium.feature_ai': 'Advanced AI-generated voice detection',
    'premium.feature_multilang': 'Multi-language support',
    'premium.button': 'Upgrade for $4.99/month',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.language': 'Language',
    'common.theme': 'Theme',
    'common.settings': 'Settings'
  },
  
  es: {
    // Main Navigation
    'nav.threat_scanner': 'Escáner de Amenazas',
    'nav.galaxy_threat_map': 'Mapa Galáctico de Amenazas',
    'nav.admin_control': 'Control de Administrador',
    'nav.partner_portal': 'Portal de Socios',
    'nav.documentation': 'Documentación',
    'nav.sdk_center': 'Centro SDK',
    
    // Main Interface
    'main.title': 'Escáner de Amenazas IA',
    'main.subtitle': 'Protección en tiempo real contra estafas y amenazas',
    'main.tabs.text': 'Análisis de Texto',
    'main.tabs.link': 'Escáner de Enlaces',
    'main.tabs.image': 'Análisis de Imagen',
    
    // Text Analysis
    'text.placeholder': 'Pega el mensaje o contenido de email sospechoso aquí...',
    'text.button': 'Escanear Amenazas',
    'text.loading': 'Analizando contenido...',
    
    // Link Analysis
    'link.placeholder': 'Introduce URL o enlace sospechoso...',
    'link.button': 'Escanear Enlace',
    'link.loading': 'Verificando seguridad del enlace...',
    
    // Image Analysis
    'image.upload': 'Subir Captura de Pantalla',
    'image.button': 'Analizar Imagen',
    'image.loading': 'Procesando imagen...',
    
    // Results
    'results.threat_detected': 'Amenaza Detectada',
    'results.safe': 'El contenido parece seguro',
    'results.confidence': 'Confianza',
    'results.threat_type': 'Tipo de Amenaza',
    'results.risk_level': 'Nivel de Riesgo',
    'results.recommendations': 'Recomendaciones',
    
    // Voice Protection
    'voice.title': 'Protección de Voz en Tiempo Real',
    'voice.status_active': 'Protegido',
    'voice.status_inactive': 'Inactivo',
    'voice.status_message_active': 'La detección en vivo está funcionando. Estás protegido.',
    'voice.status_message_inactive': 'La protección de voz está pausada',
    'voice.button_start': 'Iniciar Protección',
    'voice.button_pause': 'Pausar Protección',
    'voice.button_settings': 'Configuración',
    'voice.button_alert_log': 'Registro de Alertas',
    
    // Consent Dialog
    'consent.title': '¿Habilitar Protección de Voz en Tiempo Real?',
    'consent.description': 'Veridavo puede protegerte de llamadas de estafa en tiempo real analizando patrones de voz y detectando comportamiento sospechoso.',
    'consent.feature_realtime': 'Detección de estafas en tiempo real',
    'consent.feature_ondevice': 'Procesamiento en el dispositivo cuando sea posible',
    'consent.feature_nostorage': 'Sin almacenar audio a menos que elijas',
    'consent.button_enable': 'Habilitar Protección',
    'consent.button_ask': 'Preguntar Cada Vez',
    'consent.button_cancel': 'Cancelar',
    'consent.privacy_link': 'Información de Privacidad y Detección',
    
    // Premium Features
    'premium.title': 'Actualizar a Premium',
    'premium.feature_realtime': 'Protección de llamadas en tiempo real',
    'premium.feature_notifications': 'Notificaciones push para llamadas de estafa',
    'premium.feature_ai': 'Detección avanzada de voz generada por IA',
    'premium.feature_multilang': 'Soporte multiidioma',
    'premium.button': 'Actualizar por $4.99/mes',
    
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.close': 'Cerrar',
    'common.back': 'Atrás',
    'common.next': 'Siguiente',
    'common.language': 'Idioma',
    'common.theme': 'Tema',
    'common.settings': 'Configuración'
  },
  
  ar: {
    // Main Navigation
    'nav.threat_scanner': 'ماسح التهديدات',
    'nav.galaxy_threat_map': 'خريطة التهديدات المجرية',
    'nav.admin_control': 'تحكم المدير',
    'nav.partner_portal': 'بوابة الشركاء',
    'nav.documentation': 'التوثيق',
    'nav.sdk_center': 'مركز SDK',
    
    // Main Interface
    'main.title': 'ماسح التهديدات بالذكاء الاصطناعي',
    'main.subtitle': 'حماية في الوقت الفعلي ضد الاحتيال والتهديدات',
    'main.tabs.text': 'تحليل النص',
    'main.tabs.link': 'ماسح الروابط',
    'main.tabs.image': 'تحليل الصور',
    
    // Text Analysis
    'text.placeholder': 'الصق الرسالة المشبوهة أو محتوى البريد الإلكتروني هنا...',
    'text.button': 'مسح التهديدات',
    'text.loading': 'جارٍ تحليل المحتوى...',
    
    // Link Analysis
    'link.placeholder': 'أدخل الرابط المشبوه...',
    'link.button': 'مسح الرابط',
    'link.loading': 'جارٍ فحص أمان الرابط...',
    
    // Image Analysis
    'image.upload': 'رفع لقطة الشاشة',
    'image.button': 'تحليل الصورة',
    'image.loading': 'جارٍ معالجة الصورة...',
    
    // Results
    'results.threat_detected': 'تم اكتشاف تهديد',
    'results.safe': 'يبدو المحتوى آمناً',
    'results.confidence': 'الثقة',
    'results.threat_type': 'نوع التهديد',
    'results.risk_level': 'مستوى المخاطر',
    'results.recommendations': 'التوصيات',
    
    // Voice Protection
    'voice.title': 'حماية الصوت في الوقت الفعلي',
    'voice.status_active': 'محمي',
    'voice.status_inactive': 'غير نشط',
    'voice.status_message_active': 'الكشف المباشر يعمل. أنت محمي.',
    'voice.status_message_inactive': 'حماية الصوت متوقفة مؤقتاً',
    'voice.button_start': 'بدء الحماية',
    'voice.button_pause': 'إيقاف مؤقت للحماية',
    'voice.button_settings': 'الإعدادات',
    'voice.button_alert_log': 'سجل التنبيهات',
    
    // Consent Dialog
    'consent.title': 'تفعيل حماية الصوت في الوقت الفعلي؟',
    'consent.description': 'يمكن لـ Veridavo حمايتك من المكالمات الاحتيالية في الوقت الفعلي من خلال تحليل أنماط الصوت واكتشاف السلوك المشبوه.',
    'consent.feature_realtime': 'كشف الاحتيال في الوقت الفعلي',
    'consent.feature_ondevice': 'معالجة على الجهاز عند الإمكان',
    'consent.feature_nostorage': 'لا يتم تخزين الصوت إلا إذا اخترت',
    'consent.button_enable': 'تفعيل الحماية',
    'consent.button_ask': 'اسأل في كل مرة',
    'consent.button_cancel': 'إلغاء',
    'consent.privacy_link': 'معلومات الخصوصية والكشف',
    
    // Premium Features
    'premium.title': 'الترقية إلى Premium',
    'premium.feature_realtime': 'حماية المكالمات في الوقت الفعلي',
    'premium.feature_notifications': 'إشعارات فورية للمكالمات الاحتيالية',
    'premium.feature_ai': 'كشف متقدم للصوت المولد بالذكاء الاصطناعي',
    'premium.feature_multilang': 'دعم متعدد اللغات',
    'premium.button': 'ترقية مقابل $4.99/شهر',
    
    // Common
    'common.loading': 'جارٍ التحميل...',
    'common.error': 'خطأ',
    'common.success': 'نجح',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.close': 'إغلاق',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.language': 'اللغة',
    'common.theme': 'المظهر',
    'common.settings': 'الإعدادات'
  }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('stopscam_language') || 'en';
    changeLanguage(savedLanguage);
  }, []);

  const changeLanguage = (languageCode) => {
    if (!SUPPORTED_LANGUAGES[languageCode]) {
      console.warn(`Language ${languageCode} not supported, falling back to English`);
      languageCode = 'en';
    }

    setCurrentLanguage(languageCode);
    const langConfig = SUPPORTED_LANGUAGES[languageCode];
    setIsRTL(langConfig.rtl);
    
    // Save to localStorage
    localStorage.setItem('stopscam_language', languageCode);
    
    // Set document direction and language
    document.documentElement.dir = langConfig.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = languageCode;
    
    // Add language-specific CSS class
    document.body.className = document.body.className.replace(/\blang-\w+\b/g, '');
    document.body.classList.add(`lang-${languageCode}`);
    
    if (langConfig.rtl) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  };

  const translate = (key, variables = {}) => {
    const translations = TRANSLATIONS[currentLanguage] || TRANSLATIONS.en;
    let translation = translations[key] || TRANSLATIONS.en[key] || key;
    
    // Replace variables in translation
    Object.keys(variables).forEach(variable => {
      translation = translation.replace(`{${variable}}`, variables[variable]);
    });
    
    return translation;
  };

  const getCurrentLanguageConfig = () => {
    return SUPPORTED_LANGUAGES[currentLanguage];
  };

  const getSupportedLanguages = () => {
    return Object.values(SUPPORTED_LANGUAGES);
  };

  const contextValue = {
    currentLanguage,
    isRTL,
    changeLanguage,
    translate,
    getCurrentLanguageConfig,
    getSupportedLanguages,
    SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Higher-order component for internationalization
export const withTranslation = (Component) => {
  return function TranslatedComponent(props) {
    const { translate, isRTL, currentLanguage } = useLanguage();
    
    return (
      <Component 
        {...props} 
        t={translate}
        isRTL={isRTL}
        currentLanguage={currentLanguage}
      />
    );
  };
};

export default LanguageContext;